import { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ErrorCode, Purchases, PurchasesError } from '@revenuecat/purchases-js';
import { strogging } from '@shd/jslib/infra';
import { AppHostContext } from '@services/appHost';
import { usePaywallTracking } from '@hooks/revcat/usePaywallTracking';
import {
  PaywallPageContext,
  PaywallEvents,
  PackageName,
  SubscriptionOffering,
  PaywallPrices,
} from '@components/Paywall/paywall.types';

export const usePaywall = ({
  context,
  playerId,
  prices,
}: {
  context?: PaywallPageContext;
  playerId?: string;
  prices?: PaywallPrices;
}) => {
  const packageName = PackageName.PRO;
  const appHost = useContext(AppHostContext);
  const { trackPaywallEvent } = usePaywallTracking(context, playerId);
  const [isPaywallActive, setIsPaywallActive] = useState(false);
  const history = useHistory();

  const purchaseSubscription = useCallback(
    async (rcPackage: SubscriptionOffering) => {
      const selectedPackage =
        prices?.slhdOfferings?.all[packageName][rcPackage.term];

      if (!selectedPackage) {
        strogging.log('[useRevenueCat] Selected package is undefined');
        return;
      }

      try {
        await Purchases.getSharedInstance()
          .purchase({
            rcPackage: selectedPackage,
          })
          .then(() => {
            history.go(0);
          });
      } catch (e) {
        if (
          e instanceof PurchasesError &&
          e.errorCode == ErrorCode.UserCancelledError
        ) {
          strogging.log('[useRevenueCat] User cancelled the purchase');
        } else {
          strogging.log(`[useRevenueCat]: Error purchasing package: ${e}`);
        }
      }
    },
    [prices, packageName, history]
  );

  const handlePaywallOpen = useCallback(() => {
    if (appHost.isHosted) {
      appHost.service.notifications.showPaywall();
    } else {
      setIsPaywallActive(true);
    }
    trackPaywallEvent(PaywallEvents.OFFER_PAGE_VIEWED);
  }, [appHost, trackPaywallEvent]);

  const handlePaywallClose = useCallback(() => {
    trackPaywallEvent(PaywallEvents.OFFER_PAGE_DISMISSED);
    setIsPaywallActive(false);
  }, [trackPaywallEvent]);

  const handleUnlockAccess = useCallback(
    (offering: SubscriptionOffering) => {
      trackPaywallEvent(PaywallEvents.UNLOCK_ACCESS_CLICKED, {
        context,
        offering,
      });
      purchaseSubscription(offering);
      setIsPaywallActive(false);
    },
    [purchaseSubscription, context, trackPaywallEvent]
  );

  return {
    isPaywallActive,
    handlePaywallOpen,
    handlePaywallClose,
    handleUnlockAccess,
  };
};
